/**
 * @fileoverview Actions to manage customer data in the inbox.
 * Provides functions to load, update and handle errors related to customer data and their orders.
 * @module actions/inbox/customerDataActions
 * @category Actions Layer
 * @since 1.0.0
 * @version 2.1.0
 */
import ConversationModelMC from '../../presentation/models/inbox/ConversationModelMC';
import { INBOX_RIGHT_ACTIONS } from '../../constants/actionsConstants';
import { updateOrInsertConversationOptimized } from './conversationOptimizedActions';

/**
 * Resets the customer sidebar state when changing conversations.
 */
export const clearCustomerSidebarState = () => (dispatch) => {
  dispatch({ type: INBOX_RIGHT_ACTIONS.CLEAR_CUSTOMER_SIDEBAR });
};

/**
 * Action to get the data of a specific customer.
 *
 * Loads the customer data from ConversationModelMC and manages the loading and error states.
 */
export const setCustomerDetailsEditing = (editing) => ({
  type: INBOX_RIGHT_ACTIONS.SET_CUSTOMER_DETAILS_EDITING,
  editing: Boolean(editing)
});

export const getCustomerData = (customerId, options = {}) => async (dispatch, getState) => {
  const { force = false } = options;
  const { inboxRightReducer } = getState();

  if (!force && inboxRightReducer.isCustomerDetailsEditing) {
    return;
  }

  // Skip refetch when the sidebar already has this customer (e.g. socket-driven
  // conversation list updates). Use { force: true } for explicit refreshes.
  if (
    !force
    && inboxRightReducer.customer
    && String(inboxRightReducer.customer.id) === String(customerId)
  ) {
    return;
  }

  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_CUSTOMER_LOADING });
  try {
    const { customer } = await ConversationModelMC.getCustomerData({ customerId });
    dispatch({
      type: INBOX_RIGHT_ACTIONS.SET_CUSTOMER_DATA,
      data: {
        ...(customer || {}),
        id: customer?.id ?? customerId
      }
    });
  } catch (error) {
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_CUSTOMER_ERROR, error: error?.message || 'Error obtaining customer data' });
  }
};

/**
 * Action to get the order count of a customer.
 *
 * Performs the query of the number of orders associated with the customer and manages the loading and error states.
 */
export const getCustomerOrderCount = (customerId) => async (dispatch) => {
  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_ORDERS_LOADING });
  try {
    const { orders_count } = await ConversationModelMC.getCustomerOrderCount({ customerId });
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_ORDERS_COUNT, data: orders_count });
  } catch (error) {
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_ORDERS_ERROR, error: error?.message || 'Error obtaining the order count' });
  }
};

/**
 * Action to set the order count of a customer directly in the Redux state.
 */
export const setCustomerOrderCount = (ordersCount) => (dispatch) => {
  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_ORDERS_COUNT, data: ordersCount });
};

/**
 * Action to get the lead score of a customer for a specific platform.
 */
export const getLeadScore = (customerId, platform) => async (dispatch) => {
  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_LEAD_SCORE_LOADING, payload: true });
  try {
    const { lead_score } = await ConversationModelMC.getCustomerLeadScore({ customerId, platform });
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_LEAD_SCORE, payload: lead_score });
  } catch (error) {
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_LEAD_SCORE_ERROR, payload: error });
  }
};

/**
 * Action to get the field captures of a customer for a specific platform.
 */
export const getFieldCaptures = (customerId, platform) => async (dispatch) => {
  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_FIELD_CAPTURES_LOADING, payload: true });
  try {
    const { field_captures } = await ConversationModelMC.getCustomerFieldCaptures({ customerId, platform });
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_FIELD_CAPTURES, payload: field_captures || [] });
  } catch (error) {
    dispatch({ type: INBOX_RIGHT_ACTIONS.SET_FIELD_CAPTURES_ERROR, payload: error });
  }
};

export const setLeadScoreIfActive = (eventId, scoreData) => (dispatch, getState) => {
  const { inboxRightReducer } = getState();
  if (!inboxRightReducer.customer || String(inboxRightReducer.customer.id) !== String(eventId)) return;
  dispatch({ type: INBOX_RIGHT_ACTIONS.SET_LEAD_SCORE, payload: scoreData });
};

export const addFieldCaptureIfActive = (eventId, captureData) => (dispatch, getState) => {
  const { inboxRightReducer } = getState();
  if (!inboxRightReducer.customer || String(inboxRightReducer.customer.id) !== String(eventId)) return;
  dispatch({ type: INBOX_RIGHT_ACTIONS.ADD_FIELD_CAPTURE, payload: captureData });
};

/**
 * Action to update the data of a customer in the Redux state.
 *
 * Only updates if the current customer matches the ID provided in the data.
 */
export const updateCustomerData = (data) => (dispatch, getState) => {
  const { inboxRightReducer } = getState();

  if (inboxRightReducer.isCustomerDetailsEditing) return;
  if (!inboxRightReducer.customer || String(inboxRightReducer.customer.id) !== String(data.id)) return;
  dispatch({
    type: INBOX_RIGHT_ACTIONS.UPDATE_CUSTOMER_DATA,
    data
  });
};

/**
 * Propaga las etiquetas recién guardadas de un cliente a todo lo que las pinta.
 *
 * `POST /customers/:id/add_tags` emite un `customer_update` por socket, pero no alcanza:
 * ese evento viaja con `id = customer.id`, y los chats del microservicio se indexan por
 * su ObjectId de Mongo, así que nunca casa con la fila de la lista. Encima depender del
 * round-trip deja la UI quieta ante una acción del propio agente. Con la respuesta ya en
 * mano se aplica acá y el socket queda como refuerzo idempotente.
 *
 * Un cliente puede tener chats abiertos en varios canales a la vez: se actualizan todos
 * los que le pertenezcan, más el slot externo (chat abierto fuera de la lista filtrada).
 *
 * @param {{ customerId: string|number, tags: Array<Object> }} params
 */
export const applyCustomerTagsUpdate = ({ customerId, tags }) => (dispatch, getState) => {
  if (customerId == null || !Array.isArray(tags)) return;

  dispatch(updateCustomerData({ id: customerId, tags }));

  const { data = [], externalConversation } = getState().conversationsReducer || {};
  const target = String(customerId);
  // En los chats nativos del monolito el id de la conversación ES el del cliente; en los
  // del microservicio viene aparte en `customer_id`.
  const belongsToCustomer = (conv) => String(conv?.attributes?.customer_id ?? conv?.id) === target;

  const conversationIds = new Set(
    data.filter(belongsToCustomer).map((conv) => String(conv.id))
  );
  if (externalConversation && belongsToCustomer(externalConversation)) {
    conversationIds.add(String(externalConversation.id));
  }

  conversationIds.forEach((id) => {
    dispatch(updateOrInsertConversationOptimized({ id, changes: { tags } }));
  });
};
